import React from "react";
import { Row, Col } from "react-flexbox-grid";
import { TextField } from "react-md";
import { formatCurrency } from "../../../navigo-module/helper/formatCurrency";
import {
  EventHistoryTable,
  buildEventRows,
} from "../../../navigo-module/components/refund/EventHistoryTable";

// A PAYMENT event only counts as collected money once its `payment.status` is
// SUCCESS — mirrors hasRefundScopeFromEventHistory in navigo paymentViewModel.
// eventHistory is an append-only log (a txn can appear as INITIATED then SUCCESS,
// and a link can be generated but left PENDING/FAILED), so filtering to SUCCESS
// avoids inflating / double-counting the headline figure below.
const isSuccessfulPayment = (event) =>
  event?.type === "PAYMENT" && event.payment?.status === "SUCCESS";

// A refund counts against the total the moment it is generated — whether SUCCESS or still in
// flight. Only a FAILED refund frees the money up again. Same rule as the navigo helpers.
const isLiveRefund = (event) =>
  event?.type === "REFUND" && event.refund?.status !== "FAILED";

// An event the backend flagged as belonging to an ABANDONED attempt on this cart rather than to
// the booking it now represents. A DRAFT cart is a reusable scratchpad, so one cart id can be
// paid against more than once over its life; `eventHistory` is append-only and keeps every one of
// them. See HydratedEvent.Superseded in api-cart-service.
const isSuperseded = (event) => event?.superseded === true;

// Headline "Net amount paid" = every successful PAYMENT event (each already rolls its wallet +
// external split into `payment.amount`) less every refund that has been generated.
//
// This sums ACROSS ORDERS on purpose: a cart is routinely paid over more than one ZO- — the app
// checkout plus any Bridge-approve top-up — so the figure cannot be read off a single payment. The
// refund leg matters just as much: netting it out is what makes this the money the patient is
// actually out of pocket, rather than the gross that used to show here.
//
// Superseded events are excluded: summing across orders is right, but only across the orders of
// THIS booking. A cart that captured ₹150 for an abandoned attempt and was then reused for a
// ₹3,000 booking rendered ₹3,150 against a ₹3,000 grand total until this filter existed. The
// excluded money is real and still owed back — it is rendered in its own table below, never
// silently dropped.
//
// Clamped at 0 so an over-refund or float noise can never render as a negative headline.
const netPaidFromEvents = (eventHistory) => {
  const net = eventHistory
    .filter((event) => !isSuperseded(event))
    .reduce((sum, event) => {
      if (isSuccessfulPayment(event)) {
        return sum + (Number(event.payment.amount) || 0);
      }
      if (isLiveRefund(event)) {
        return sum - (Number(event.refund.amount) || 0);
      }
      return sum;
    }, 0);

  return net > 0 ? net : 0;
};

// Headline "Zyla order ID" = the id of the most recent successful PAYMENT event.
// The backend returns eventHistory newest-first, so the first successful PAYMENT
// event carries the latest paid ZO- order id (top-ups append after the initial
// app-user payment).
// const latestOrderId = (eventHistory) =>
//   eventHistory.find(isSuccessfulPayment)?.id || "-";

// Cart verification "Payment details" section. Driven by the cart's hydrated
// `eventHistory` (fetchCartById with { eventHistory: true }) and rendered with
// the same EventHistoryTable used on the navigo appointment screen.
const PaymentDetailsSection = ({ eventHistory = [] }) => {
  if (!eventHistory.length) return null;

  const currentEvents = eventHistory.filter((e) => !isSuperseded(e));
  const supersededEvents = eventHistory.filter(isSuperseded);

  const rows = buildEventRows(currentEvents);
  const supersededRows = buildEventRows(supersededEvents);
  const netPaid = netPaidFromEvents(eventHistory);
  // const orderId = latestOrderId(eventHistory);

  return (
    <div>
      <div>
        <h3>{`Payment details`}</h3>
        {/* Zyla order ID — commented out: a cart can be paid over several ZO-s, so one field could
            only ever name one of them. The table's Order ID/Refund ID column carries them all.
        <Row>
          <Col xs={12} sm={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">Zyla order ID</div>
              <TextField value={orderId} lineDirection="left" disabled />
            </div>
          </Col>
        </Row>

        <br />
        */}

        <Row>
          <Col xs={12} sm={3}>
            <div className="cart-module-create-service-field-container">
              <div className="cart-module-label">Net amount paid</div>
              <TextField
                value={formatCurrency(netPaid) || "-"}
                lineDirection="left"
                disabled
              />
            </div>
          </Col>
        </Row>

        <br />
        <EventHistoryTable rows={rows} />

        {/* Money captured against a booking this cart no longer represents — the patient was
            charged and never got that booking, so it is still owed back. Kept visible and
            separate rather than netted into the headline above: it is not part of what this
            cart costs, but it is not nothing either, and nothing else in Bridge surfaces it. */}
        {supersededRows.length > 0 && (
          <div>
            <br />
            <h4>{`Payments from earlier attempts`}</h4>
            <div className="cart-module-label">
              {`Captured on this cart before it was reused for the current booking. Not included in the net amount above — review whether a refund is due.`}
            </div>
            <br />
            <EventHistoryTable rows={supersededRows} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentDetailsSection;
